import type { Nav, Screen } from "./dom";
import { HomeScreen } from "./home";
import { CampaignScreen } from "./campaign";
import { PlayScreen } from "./play";
import { PuzzleScreen } from "./puzzles";
import { SkillsScreen } from "./skills";
import { SettingsScreen } from "./settings";
import { LeaderboardScreen } from "./leaderboard";
import { AchievementsScreen } from "./achievements";
import { CollectionScreen } from "./collection";
import { CosmeticsScreen } from "./cosmetics";

/**
 * Persisted route the app boots into next time. Lets mobile users return to
 * the screen they were on after the browser drops the tab from memory.
 * Mid-game state isn't preserved — the play screen reopens with a fresh run.
 */
interface PersistedRoute {
  name:
    | "home"
    | "campaign"
    | "play"
    | "endless"
    | "daily"
    | "puzzles"
    | "playPuzzle"
    | "skills"
    | "settings"
    | "leaderboard"
    | "achievements"
    | "collection"
    | "cosmetics";
  stageId?: string;
  puzzleId?: string;
  savedAt: number;
}

const ROUTE_KEY = "cm.route.v1";
/** Drop saved routes older than this; stale deep-links feel worse than landing home. */
const ROUTE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function loadRoute(): PersistedRoute | null {
  try {
    const raw = localStorage.getItem(ROUTE_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as PersistedRoute;
    if (!r || !r.name) return null;
    if (Date.now() - (r.savedAt ?? 0) > ROUTE_MAX_AGE_MS) return null;
    return r;
  } catch {
    return null;
  }
}

function saveRoute(r: Omit<PersistedRoute, "savedAt">): void {
  try {
    localStorage.setItem(ROUTE_KEY, JSON.stringify({ ...r, savedAt: Date.now() }));
  } catch {
    /* ignore */
  }
}

/** Top-level screen router. Mounts one screen at a time into the host element. */
export class App implements Nav {
  private current: Screen | null = null;

  constructor(private host: HTMLElement) {}

  private show(screen: Screen, route: Omit<PersistedRoute, "savedAt">): void {
    this.current?.unmount();
    this.host.innerHTML = "";
    this.current = screen;
    screen.mount(this.host);
    this.host.scrollTop = 0;
    saveRoute(route);
  }

  /** Boot into the last screen the player was on (or home if nothing saved). */
  boot(): void {
    const r = loadRoute();
    if (!r) return this.home();
    switch (r.name) {
      case "campaign": return this.campaign();
      case "play": return r.stageId ? this.play(r.stageId) : this.campaign();
      case "endless": return this.endless();
      case "daily": return this.daily();
      case "puzzles": return this.puzzles();
      case "playPuzzle": return r.puzzleId ? this.playPuzzle(r.puzzleId) : this.puzzles();
      case "skills": return this.skills();
      case "settings": return this.settings();
      case "leaderboard": return this.leaderboard();
      case "achievements": return this.achievements();
      case "collection": return this.collection();
      case "cosmetics": return this.cosmetics();
      case "home":
      default: return this.home();
    }
  }

  home(): void {
    this.show(new HomeScreen(this), { name: "home" });
  }
  campaign(): void {
    this.show(new CampaignScreen(this), { name: "campaign" });
  }
  play(stageId: string): void {
    this.show(new PlayScreen(this, { stageId }), { name: "play", stageId });
  }
  endless(): void {
    this.show(new PlayScreen(this, {}), { name: "endless" });
  }
  daily(): void {
    this.show(new PlayScreen(this, { daily: true }), { name: "daily" });
  }
  puzzles(): void {
    this.show(new PuzzleScreen(this), { name: "puzzles" });
  }
  playPuzzle(puzzleId: string): void {
    this.show(new PlayScreen(this, { puzzleId }), { name: "playPuzzle", puzzleId });
  }
  skills(): void {
    this.show(new SkillsScreen(this), { name: "skills" });
  }
  settings(): void {
    this.show(new SettingsScreen(this), { name: "settings" });
  }
  leaderboard(): void {
    this.show(new LeaderboardScreen(this), { name: "leaderboard" });
  }
  achievements(): void {
    this.show(new AchievementsScreen(this), { name: "achievements" });
  }
  collection(): void {
    this.show(new CollectionScreen(this), { name: "collection" });
  }
  cosmetics(): void {
    this.show(new CosmeticsScreen(this), { name: "cosmetics" });
  }
}
